// Корекция на вече издадена фактура чрез кредитно (намаление) или дебитно
// (увеличение) известие — по същата поредица, без триене/редакция на оригинала.

import React, { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { api, money } from '../../api';
import { showError, showSuccess } from '../../toast';
import { Button, Card, COLORS, Input, Label, Muted, Row, Screen, Title } from '../../ui';
import { t } from '../../i18n';

type Line = { name: string; amount: string };

export default function CorrectInvoiceScreen({ route, navigation }: any) {
  const { invoiceId, number } = route.params;
  const [kind, setKind] = useState<'CREDIT_NOTE' | 'DEBIT_NOTE'>('CREDIT_NOTE');
  const [method, setMethod] = useState<'bank' | 'cash'>('bank');
  const [lines, setLines] = useState<Line[]>([{ name: t('Корекция по фактура № {number}', { number }), amount: '' }]);
  const [loading, setLoading] = useState(false);

  const setLine = (i: number, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { name: '', amount: '' }]);
  const removeLine = (i: number) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const parsed = lines
    .map((l) => ({ name: l.name.trim(), cents: Math.round(parseFloat(l.amount.replace(',', '.')) * 100) }))
    .filter((l) => l.name && Number.isFinite(l.cents) && l.cents > 0);
  const total = parsed.reduce((s, l) => s + l.cents, 0);

  const submit = async () => {
    setLoading(true);
    try {
      const res = await api(`/invoices/${invoiceId}/correction`, {
        body: {
          kind,
          paymentMethod: method,
          lines: parsed.map((l) => ({ name: l.name, unitPriceCents: l.cents })),
        },
      });
      showSuccess(
        kind === 'CREDIT_NOTE'
          ? t('Кредитно известие № {number} е издадено.', { number: res.invoice.number })
          : t('Дебитно известие № {number} е издадено.', { number: res.invoice.number })
      );
      navigation.replace('Invoices');
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Screen>
      <Title>{t('Корекция на фактура № {number}', { number })}</Title>
      <Muted>
        {t('Оригиналната фактура остава непроменена. Издава се известие по същата поредица, което я коригира.')}
      </Muted>

      <Card>
        <Label>{t('Вид корекция')}</Label>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          {(
            [
              ['CREDIT_NOTE', t('Кредитно (намаление)')],
              ['DEBIT_NOTE', t('Дебитно (увеличение)')],
            ] as const
          ).map(([key, lbl]) => (
            <Pressable
              key={key}
              onPress={() => setKind(key)}
              style={{
                flex: 1,
                padding: 12,
                borderRadius: 8,
                alignItems: 'center',
                backgroundColor: kind === key ? COLORS.primary : COLORS.bg,
                borderWidth: 1,
                borderColor: kind === key ? COLORS.primary : COLORS.border,
              }}
            >
              <Text style={{ color: kind === key ? '#fff' : COLORS.text, textAlign: 'center' }}>{lbl}</Text>
            </Pressable>
          ))}
        </View>
      </Card>

      <Label>{t('Редове на корекцията')}</Label>
      {lines.map((l, i) => (
        <Card key={i}>
          <Label>{t('Описание')}</Label>
          <Input value={l.name} onChangeText={(t) => setLine(i, { name: t })} placeholder={t('Напр. Отстъпка за наем')} />
          <Label>{t('Сума (данъчна основа, €)')}</Label>
          <Input
            value={l.amount}
            onChangeText={(t) => setLine(i, { amount: t })}
            placeholder="0.00"
            keyboardType="decimal-pad"
          />
          {lines.length > 1 && (
            <Button title={t('Премахни реда')} variant="danger" onPress={() => removeLine(i)} />
          )}
        </Card>
      ))}
      <Button title={t('+ Добави ред')} variant="secondary" onPress={addLine} />

      <Card>
        <Label>{t('Начин на плащане')}</Label>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          {(
            [
              ['bank', t('По банка')],
              ['cash', t('В брой')],
            ] as const
          ).map(([key, lbl]) => (
            <Pressable
              key={key}
              onPress={() => setMethod(key)}
              style={{
                flex: 1,
                padding: 12,
                borderRadius: 8,
                alignItems: 'center',
                backgroundColor: method === key ? COLORS.primary : COLORS.bg,
                borderWidth: 1,
                borderColor: method === key ? COLORS.primary : COLORS.border,
              }}
            >
              <Text style={{ color: method === key ? '#fff' : COLORS.text }}>{lbl}</Text>
            </Pressable>
          ))}
        </View>
        <Row left={t('Общо (данъчна основа)')} right={money(total)} />
        <Muted>{t('ДДС се начислява според настройката ви (Профил → Данни за фактури).')}</Muted>
      </Card>

      <Button
        title={
          kind === 'CREDIT_NOTE'
            ? t('Издай кредитно известие ({amount})', { amount: money(total) })
            : t('Издай дебитно известие ({amount})', { amount: money(total) })
        }
        onPress={submit}
        disabled={parsed.length === 0}
        loading={loading}
      />
    </Screen>
  );
}
